'use client';

import React, { useState } from 'react';
import { SectionLayout } from '@/components/shared/section-layout';
import { TabsContent } from '@/components/ui/tabs';
import { BudgetsOverview } from './components/budgets-overview';
import { BudgetTable } from './components/budget-table';
import { useProject } from '@/components/ProjectSelector';
import { GenericTabBar } from '@/styles/GenericTabBar';
import { useVisibleTabs } from '@/hooks/useVisibleTabs';
import { NothingAvailable } from '@/components/NavigationUnavailable';
import type { NavKey } from '@/lib/navigation-catalog';

// which tabs a role gets is the navigation matrix (nav_visibility), one element per tab
const TAB_KEYS: Record<string, NavKey> = { overview: 'cost_management.budgets.tab.overview', budget: 'cost_management.budgets.tab.budget' };
const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'budget', label: 'Budget' },
];

/**
 * Budget: ONE table per project. Overview lists every project (its budget, committed, billed); Budget is the selected project's
 * table. The global project selector is the only project context -- opening a project from the Overview just sets it.
 */
export function BudgetsPage() {
  const visible = useVisibleTabs(TABS, TAB_KEYS);
  const [requestedTab, setActiveTab] = useState('overview');
  const activeTab = visible.activeOf(requestedTab);
  const { selectedProject, setSelectedProject } = useProject();

  const openBudget = (projectId: string) => {
    setSelectedProject(projectId);
    if (visible.has('budget')) setActiveTab('budget');
  };

  if (activeTab === undefined) return <SectionLayout title="Budgets"><NothingAvailable message="No section of Budgets is available for your account." /></SectionLayout>;

  return (
    <SectionLayout title="Budgets">
      <GenericTabBar tabs={visible.tabs} activeTab={activeTab} onTabChange={setActiveTab} className="space-y-6">
        {visible.has('overview') && (
          <TabsContent value="overview" className="space-y-6 mt-6">
            <BudgetsOverview onOpenBudget={openBudget} />
          </TabsContent>
        )}

        {visible.has('budget') && (
          <TabsContent value="budget" className="space-y-6 mt-6">
            {/* keyed by project: changing the project saves the pending edits of the old one and loads the new one */}
            <BudgetTable key={selectedProject} />
          </TabsContent>
        )}
      </GenericTabBar>
    </SectionLayout>
  );
}
